import React from "react";
import { Box, Button } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { UserDTO } from "api";
import { EsgDeleteIcon, EsgLinkIcon, SendInviteIcon } from "assets";
import {
  AutocompleteField,
  Avatar,
  COLORS,
  EsgPrimaryLabel,
  Label14SemiBold,
  Tooltip,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { USER_ROLES, USER_ROLES_NAMES } from "recoils";
import { getInitials } from "utils";
import { EMPTY_CELL_VALUE } from "consts";

import { fullNameFromUser, userTitleFromUser } from "./utils";
import {
  ROLE_SELECT_OPTIONS,
  ROLE_SELECT_OPTIONS_APPROVAL_FLOW,
  STATUS_STYLES,
} from "./consts";
import { ManageUserColumnsParams } from "./types";

export const getManageUserColumns = (
  params: ManageUserColumnsParams,
): MRT_ColumnDef<UserDTO>[] => {
  const {
    isApprovalFlowActive,
    isSitesLoading,
    sitesOptions,
    sitesOptionsForAdminUsers,
    isSuperAdminViewer,
    getCurrentSitesForUser,
    onSitesSelectionChange,
    getRoleSelectValue,
    getIsAdminDropdownSelected,
    onRoleAutocompleteChange,
    onUpdateUser,
    onDeleteUser,
    disableDelete,
    notify,
    onSendInvitation,
  } = params;

  return [
    {
      id: "icon",
      header: "",
      size: 56,
      Cell: ({ row }) => {
        const { firstName, lastName } = row.original;
        const initials = getInitials(
          `${firstName ?? ""} ${lastName ?? ""}`.trim() || null,
        );
        return (
          <Avatar
            sx={{
              bgcolor: COLORS.Romance,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EsgPrimaryLabel sx={{ color: "white" }}>
              {initials ?? "?"}
            </EsgPrimaryLabel>
          </Avatar>
        );
      },
      enableGlobalFilter: false,
    },
    {
      id: "fullName",
      header: "Name",
      accessorFn: (row) => fullNameFromUser(row) ?? "",
      Cell: ({ cell }) => (
        <Label14SemiBold>{cell.getValue<string>() || ""}</Label14SemiBold>
      ),
    },
    {
      id: "email",
      header: "Email",
      accessorKey: "email",
      Cell: ({ cell }) => (
        <Label14SemiBold>{cell.getValue<string>()}</Label14SemiBold>
      ),
    },
    {
      id: "title",
      header: "Title",
      accessorFn: (row) => userTitleFromUser(row),
      Cell: ({ row }) => {
        const userData = row.original;
        const isAdmin =
          userData.roles?.includes(USER_ROLES.SUPER_ADMIN) ||
          userData.roles?.includes(USER_ROLES.MODULE_ADMIN);

        return (
          <AutocompleteField
            textFieldProps={{
              sx: {
                width: "220px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.Romance,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.Romance,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.Romance,
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000000",
                },
              },
            }}
            autocompleteProps={{
              value:
                getRoleSelectValue(userData) ||
                (isAdmin
                  ? USER_ROLES_NAMES[USER_ROLES.MODULE_ADMIN]
                  : USER_ROLES_NAMES[userData.roles?.[0]]),
              onChange: (_, role) => {
                if (role) {
                  const payload = {
                    id: userData?.id,
                    siteIds: userData?.sites,
                    roles: [String(role)?.replace(" ", "_")],
                    email: userData?.email,
                  };
                  onUpdateUser(payload);
                }
                onRoleAutocompleteChange(userData, role as string | null);
              },
              options:
                isApprovalFlowActive === "true"
                  ? ROLE_SELECT_OPTIONS_APPROVAL_FLOW
                  : ROLE_SELECT_OPTIONS,
            }}
          />
        );
      },
    },
    {
      id: "sites",
      header: "Sites",
      accessorFn: (row) => row,
      Cell: ({ row }) => {
        const userData = row.original;
        const isAdmin =
          userData.roles?.includes(USER_ROLES.SUPER_ADMIN) ||
          userData.roles?.includes(USER_ROLES.MODULE_ADMIN);

        return (
          <EsgMultiselectControlledField
            label=""
            disabled={
              isSitesLoading || isAdmin || getIsAdminDropdownSelected(userData)
            }
            selectedValues={
              isAdmin
                ? sitesOptionsForAdminUsers
                : getCurrentSitesForUser(userData)
            }
            setSelectedValues={(data: any) => {
              onSitesSelectionChange(userData, data);
            }}
            options={sitesOptions}
            isAllSelectedOnEmptySelection={
              isAdmin || getIsAdminDropdownSelected(userData)
            }
            type="dashboard"
          />
        );
      },
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => (row.enabled === true ? "Active" : "Inactive"),
      Cell: ({ row }) => {
        const { invitationLink, status } = row.original;

        const statusNorm = status?.toLowerCase() ?? "";
        const inviteSent = statusNorm === "invite sent";
        const withoutInvitationOrExpired =
          statusNorm === "invite expired" || statusNorm === "no invitation";

        if (!status) return EMPTY_CELL_VALUE;

        const statusStyle = STATUS_STYLES[statusNorm] ?? {
          backgroundColor: "transparent",
          color: "#000",
        };

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "fit-content",
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
                padding: "0 12px",
                borderRadius: "8px",
                fontWeight: "500",
              }}
            >
              {status}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              {isSuperAdminViewer && inviteSent && (
                <Tooltip title="Copy Invitation Link">
                  <Button
                    sx={{
                      maxWidth: "20px",
                      minWidth: "20px",
                      padding: 0,
                      "& svg path": { stroke: COLORS.Romance },
                    }}
                    onClick={() => {
                      const link = invitationLink;
                      if (link) {
                        navigator.clipboard.writeText(link).catch(() => {
                          notify.error(
                            "Something went wrong with the invitation link.",
                          );
                        });
                        notify.success("Invitation link copied.");
                      }
                    }}
                  >
                    <EsgLinkIcon color={COLORS.Romance} />
                  </Button>
                </Tooltip>
              )}
              {isSuperAdminViewer && withoutInvitationOrExpired && (
                <Tooltip title="Resend Invitation">
                  <Button
                    sx={{
                      maxWidth: "20px",
                      minWidth: "20px",
                      padding: 0,
                      "& svg path": { fill: COLORS.Romance },
                    }}
                    onClick={() => onSendInvitation(row.original)}
                  >
                    <SendInviteIcon />
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Delete User">
                <Button
                  sx={{
                    maxWidth: "20px",
                    minWidth: "20px",
                    padding: 0,
                  }}
                  onClick={() => onDeleteUser(row.original)}
                  disabled={disableDelete(row.original)}
                >
                  <EsgDeleteIcon color="red" />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        );
      },
    },
  ];
};
